import { z } from 'zod';
import { NumberProvider } from '../enums.js';
import { E164, TimestampSchema } from '../primitives.js';
/**
 * `numberPool/{e164}` — inventory of the shared inbound numbers we own (doc 18).
 *
 * Deliberately NOT the same document as `phoneNumbers/{e164}`. That one is a
 * routing index, read on the hot path of every inbound call and shaped for a
 * single-key lookup; this one is lifecycle bookkeeping — how many businesses sit
 * on the number, whether it is still taking new ones, which vendor to talk to
 * when retiring it. Conflating them would put churny counters in the document
 * the call path reads, and put call-path constraints on the inventory.
 *
 * Both are written together when a number is registered, and the gateway only
 * ever reads the routing one.
 */
export const NumberPoolEntrySchema = z.object({
    phoneNumber: E164,
    /** ISO country. Businesses are only ever assigned a number from their own
     * country: a foreign one turns the owner's divert into an international call
     * on every customer call (doc 13a D2). */
    isoCountry: z.string().length(2),
    provider: NumberProvider,
    /** Vendor handles, for retirement. Which one is set follows `provider`. */
    twilioNumberSid: z.string().nullable().default(null),
    telnyxNumberId: z.string().nullable().default(null),
    /**
     * `active` — accepting new businesses.
     * `draining` — still serving its existing businesses, but assigned no more.
     *   The state a number sits in while it is being retired; it must not be
     *   released until every business on it has moved.
     * `retired` — no businesses left, released or releasable.
     */
    status: z.enum(['active', 'draining', 'retired']).default('active'),
    /** How many businesses currently route through this number. Maintained
     * transactionally by assignment/release so two concurrent signups cannot
     * both take the last slot. */
    assignedCount: z.number().int().min(0).default(0),
    /**
     * Maximum businesses on this number. Not a technical limit — a DID carries
     * many simultaneous calls — but a blast-radius one: whatever can take a
     * number out (a carrier block, a spam listing) takes out everyone on it.
     * See doc 18 D5.
     */
    capacity: z.number().int().min(1).default(25),
    createdAt: TimestampSchema.optional(),
    updatedAt: TimestampSchema.optional(),
});
/** Default businesses-per-number. Deliberately conservative; revisit on real
 * concurrency data rather than on intuition (doc 18 D5, §11). */
export const DEFAULT_POOL_CAPACITY = 25;
//# sourceMappingURL=numberPool.js.map